import { useEffect, useCallback } from 'react'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { readBranchLink } from '@/lib/branch'
import {
  BRANCH_ACTION_PROMOTION_PAGE,
  BRANCH_ACTION_LANDING_PAGE,
  BRANCH_ACTION_PDP_PAGE,
  BRANCH_ACTION_CHANGE_STORE,
  BRANCH_ACTION_MEMBERSHIP,
  BRANCH_ACTION_PLAN,
  BRANCH_ACTION_STORES,
  BRANCH_ACTION_MANUAL_RESCHEDULE,
  BRANCH_ACTION_GO_TO_CART,
  BRANCH_ACTION_ORDER_PROGRESS,
  BRANCH_ACTION_MANUAL_RESCHEDULE_ORDER,
  BRANCH_ACTION_SEARCH_PRODUCTS,
  BRANCH_ACTION_GO_TO_SEARCH,
  BRANCH_ACTION_GO_TO_SEARCH_QUERY,
} from '@/features/shared/constants/branch'
import { routes, getRetailerUrl } from '@shared/constants/routes'
import { getDeepLinkRedirectRoute } from '@/lib/branch/utils'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { useMembershipModals } from '@/features/account/components/MembershipManagement/useMembershipModals'
import { useStoreId, useIsMember, useUser } from '@/features/account/services/User/hooks'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useMutationSelectStore } from '@/features/shop/services/ShoppingStore/mutations'
import { logError } from '@/features/shared/utils/logger'
import { DynamicChooseStoreModal } from '@/features/account/components/Modal/ChooseStoreModal/DynamicChooseStoreModal'
import { DynamicDeepLinkChangeStoreModal } from '@/features/shop/components/Modal/DeepLinkChangeStoreModal/DynamicDeepLinkChangeStoreModal'
import { useUserShoppingStore } from '@/features/shop/services/ShoppingStore/hooks'
import { Row } from '@shipt/design-system-layouts'

export const BranchContainer = () => {
  const router = useSegwayRouter()
  const storeId = useStoreId()
  const isMember = useIsMember()
  const { openMembershipSelectionModal } = useMembershipModals()
  const { isSessionLoading, isSessionValid } = useSession()
  const { openDialog } = useHandleDialog()
  const user = useUser()
  const { default_shopping_address_id } = user ?? {}
  const { data: shoppingStore } = useUserShoppingStore()

  const { mutateAsync: selectStore } = useMutationSelectStore()

  const handleStoreUpdateAndCartNav = useCallback(
    async ({
      branch_store_id,
      branch_address_id,
    }: {
      branch_store_id: number
      branch_address_id: number
    }) => {
      try {
        if (branch_address_id !== default_shopping_address_id || branch_store_id !== storeId) {
          await selectStore({
            store: { id: branch_store_id },
            addressId: branch_address_id,
          })
        }
        return router.replace(routes.CART.url)
      } catch (e) {
        logError(
          new Error('There was an error selecting the store/address from branch parameters', {
            cause: e,
          }),
          {
            fetcherName: 'useMutationSelectStore',
            userStoreId: storeId,
            store_id: branch_store_id,
            address_id: branch_address_id,
            default_shopping_address_id,
          },
        )
        return router.replace(routes.GLOBAL_HOMEPAGE.url)
      }
    },
    [default_shopping_address_id, router, selectStore, storeId],
  )

  useEffect(() => {
    if (isSessionLoading) return
    readBranchLink().then((branchData) => {
      const deepLinkData = branchData.data_parsed
      const {
        query,
        action,
        order_id,
        store_id,
        landingPageSlug,
        promotion_id,
        product_id,
        address_id,
        category_id,
      } = deepLinkData
      const redirectRoute = getDeepLinkRedirectRoute(deepLinkData, shoppingStore?.name)
      const routeToHome = () => router.replace(getRetailerUrl(shoppingStore?.name))

      const handleDeepLinkRedirect = () => {
        // if users selected store matches deeplink store, just redirect to route
        if (storeId === Number(store_id)) {
          return router.replace(redirectRoute)
        } else {
          //otherwise, open open modal to prompt user to change retailer
          openDialog(DynamicDeepLinkChangeStoreModal, {
            deepLinkData,
          })
          return routeToHome()
        }
      }

      switch (true) {
        case action === BRANCH_ACTION_PLAN:
        case action === BRANCH_ACTION_MEMBERSHIP: {
          if (!isMember) {
            openMembershipSelectionModal()
          }
          return routeToHome()
        }
        case action === BRANCH_ACTION_CHANGE_STORE && Boolean(store_id):
        case action === BRANCH_ACTION_PDP_PAGE && Boolean(product_id):
        case action === BRANCH_ACTION_LANDING_PAGE && Boolean(landingPageSlug):
        case action === BRANCH_ACTION_PROMOTION_PAGE && Boolean(promotion_id):
        case action === BRANCH_ACTION_GO_TO_SEARCH && Boolean(category_id):
        case action === BRANCH_ACTION_SEARCH_PRODUCTS && Boolean(category_id):
        case action === BRANCH_ACTION_GO_TO_SEARCH_QUERY && Boolean(query): {
          if (!storeId || !shoppingStore?.name) return
          return store_id ? handleDeepLinkRedirect() : routeToHome()
        }
        case action === BRANCH_ACTION_MANUAL_RESCHEDULE && Boolean(order_id): {
          return router.replace(redirectRoute)
        }
        case action === BRANCH_ACTION_MANUAL_RESCHEDULE_ORDER && Boolean(order_id): {
          return router.replace(redirectRoute)
        }
        case action === BRANCH_ACTION_STORES: {
          openDialog(DynamicChooseStoreModal)
          return routeToHome()
        }
        case action === BRANCH_ACTION_GO_TO_CART && !!store_id && !!address_id: {
          if (isSessionValid) {
            if (!user?.id) {
              return
            }
            if (default_shopping_address_id && storeId) {
              return handleStoreUpdateAndCartNav({
                branch_store_id: +store_id,
                branch_address_id: +address_id,
              })
            }
            return router.replace(routes.GLOBAL_HOMEPAGE.url)
          }
          return routeToHome()
        }
        case action === BRANCH_ACTION_ORDER_PROGRESS && Boolean(order_id): {
          return router.replace(redirectRoute)
        }
        default: {
          return routeToHome()
        }
      }
    })
  }, [
    router,
    storeId,
    isMember,
    openMembershipSelectionModal,
    isSessionLoading,
    isSessionValid,
    openDialog,
    handleStoreUpdateAndCartNav,
    user?.id,
    default_shopping_address_id,
    shoppingStore?.name,
  ])

  return (
    <Row align="center" justify="center" marginTop="5xl">
      <LoadingIndicator size="xl" />
    </Row>
  )
}
